import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useShop } from "../context/ShopContext";
import {
  getAllActiveProducts,
  getProductImageUrl
} from "../services/products";
import { stripGreekTonos } from "../utils/typography";
import { sections } from "../data/sections";

const headerSections = sections.filter(
  (section) => section.id !== "cosmidis-coating" && !section.path.startsWith("http")
);

function SectionDropdown({ mobile = false }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    function closeOnOutsideClick(event) {
      if (!menuRef.current?.contains(event.target)) setOpen(false);
    }

    function closeOnEscape(event) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("mousedown", closeOnOutsideClick);
    window.addEventListener("keydown", closeOnEscape);

    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  return (
    <div
      ref={menuRef}
      className={`section-dropdown${mobile ? " mobile-section-dropdown" : ""}${open ? " open" : ""}`}
    >
      <button
        type="button"
        className="section-dropdown-trigger"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        Ενότητες <span aria-hidden="true" />
      </button>
      <div className="section-dropdown-menu">
        {headerSections.map((section) => (
          <Link key={section.id} to={section.path} onClick={() => setOpen(false)}>
            {section.title}
          </Link>
        ))}
      </div>
    </div>
  );
}

function normalizeSearchValue(value) {
  return stripGreekTonos(value).toLowerCase().trim();
}

function getSearchMatches(products, query) {
  const normalizedQuery = normalizeSearchValue(query);

  if (normalizedQuery.length < 2) return [];

  return products
    .filter((product) => {
      const name = normalizeSearchValue(product.name);
      const productCode = normalizeSearchValue(product.productCode);

      return name.includes(normalizedQuery) || productCode.includes(normalizedQuery);
    })
    .sort((a, b) => (a.name || "").localeCompare(b.name || "", "el"))
    .slice(0, 6);
}

export default function Header() {
  const { user, isAdmin, logout } = useAuth();
  const { cart, cartCount, cartTotal, wishlistCount, showNotice } = useShop();
  const location = useLocation();
  const navigate = useNavigate();
  const [accountOpen, setAccountOpen] = useState(false);
  const [cartPreviewOpen, setCartPreviewOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchProducts, setSearchProducts] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");

  const currentPath = `${location.pathname}${location.search}${location.hash}`;
  const loginState = { returnTo: currentPath };
  const searchMatches = getSearchMatches(searchProducts, searchQuery);

  async function handleLogout() {
    await logout();
    showNotice("Αποσυνδεθήκατε με επιτυχία.", { persist: true });
    window.location.assign("/");
  }

  function handleCartClick(event) {
    const mobileCartPreview =
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 680px)").matches;

    if (!mobileCartPreview) return;

    event.preventDefault();
    setCartPreviewOpen((open) => !open);
  }

  const loadSearchProducts = useCallback(async function loadSearchProducts() {
    if (searchProducts.length > 0 || searchLoading) return;

    setSearchLoading(true);
    setSearchError("");

    try {
      setSearchProducts(await getAllActiveProducts());
    } catch (error) {
      console.error(error);
      setSearchError("Δεν ήταν δυνατή η αναζήτηση.");
    } finally {
      setSearchLoading(false);
    }
  }, [searchLoading, searchProducts.length]);

  function goToProduct(product) {
    if (!product?.slug) return;

    setSearchQuery("");
    setSearchOpen(false);
    navigate(`/product/${product.slug}`);
  }

  function handleSearchSubmit(event) {
    event.preventDefault();

    if (searchMatches.length > 0) {
      goToProduct(searchMatches[0]);
      return;
    }

    if (searchQuery.trim()) {
      setSearchOpen(true);
      loadSearchProducts();
    }
  }

  useEffect(() => {
    setAccountOpen(false);
    setCartPreviewOpen(false);
    setSearchOpen(false);
  }, [location.pathname, user]);

  useEffect(() => {
    if (searchQuery.trim().length >= 2) {
      setSearchOpen(true);
      loadSearchProducts();
    }
  }, [loadSearchProducts, searchQuery]);

  return (
    <>
      <div className="utility-bar">
        <div className="utility-bar-inner">
          <span aria-hidden="true" />
          <nav className="utility-links" aria-label="Βοηθητική πλοήγηση">
            <Link to="/contact">Επικοινωνία</Link>
            <SectionDropdown />
            {isAdmin && <Link to="/admin">Admin</Link>}
            {user ? (
              <div className={accountOpen ? "account-menu open" : "account-menu"}>
                <button
                  type="button"
                  className="account-trigger signed-in"
                  aria-expanded={accountOpen}
                  onClick={() => setAccountOpen((open) => !open)}
                >
                  Γειά σου {user.email}
                </button>

                <div className="account-dropdown">
                  <Link to="/profile" onClick={() => setAccountOpen(false)}>
                    Προφίλ
                  </Link>
                  <Link to="/orders" onClick={() => setAccountOpen(false)}>
                    Παραγγελίες
                  </Link>
                  <button type="button" onClick={handleLogout}>
                    Έξοδος
                  </button>
                </div>
              </div>
            ) : (
              <Link to="/login" state={loginState}>
                Σύνδεση
              </Link>
            )}
          </nav>
        </div>
      </div>

      <header className="site-header">
        <div className="header-top">
          <Link to="/" className="brand">
            <img src="/images/logos/oss-shop-header.png" alt="Official Store Supplies Logo" />
          </Link>

          <SectionDropdown mobile />

          <nav className="header-actions" aria-label="Κεντρική πλοήγηση">
            <form
              className="header-search"
              role="search"
              onSubmit={handleSearchSubmit}
              onFocus={() => {
                if (searchQuery.trim().length >= 2) setSearchOpen(true);
              }}
              onBlur={() => {
                window.setTimeout(() => setSearchOpen(false), 120);
              }}
            >
              <span className="search-icon" aria-hidden="true" />
              <input
                type="search"
                value={searchQuery}
                placeholder="Αναζήτηση"
                aria-label="Αναζήτηση προϊόντων"
                onChange={(event) => setSearchQuery(event.target.value)}
              />

              {searchOpen && searchQuery.trim().length >= 2 && (
                <div className="header-search-results">
                  {searchLoading ? (
                    <p>Αναζήτηση...</p>
                  ) : searchError ? (
                    <p>{searchError}</p>
                  ) : searchMatches.length > 0 ? (
                    searchMatches.map((product) => (
                      <button
                        type="button"
                        key={product.id}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => goToProduct(product)}
                      >
                        {getProductImageUrl(product) && (
                          <img
                            src={getProductImageUrl(product)}
                            alt={product.name}
                            loading="lazy"
                          />
                        )}
                        <span>
                          <strong>{product.name}</strong>
                          {product.productCode && <small>{product.productCode}</small>}
                        </span>
                      </button>
                    ))
                  ) : (
                    <p>Δεν βρέθηκε προϊόν.</p>
                  )}
                </div>
              )}
            </form>

            <Link to="/wishlist" className="header-widget" aria-label="Wishlist">
              <span className="heart-icon" aria-hidden="true">♥</span>
              {wishlistCount > 0 && <small>{wishlistCount}</small>}
            </Link>

            <div className={cartPreviewOpen ? "cart-preview-shell open" : "cart-preview-shell"}>
              <Link
                to="/cart"
                className="header-widget"
                aria-label="Καλάθι"
                aria-expanded={cartPreviewOpen}
                onClick={handleCartClick}
              >
                <span className="cart-icon" aria-hidden="true" />
                {cartCount > 0 && <small>{cartCount}</small>}
              </Link>

              <div className="cart-preview" aria-label="Προεπισκόπηση καλαθιού">
                {cart.length === 0 ? (
                  <>
                    <p>Το καλάθι είναι άδειο.</p>
                    <Link
                      to="/cart"
                      className="cart-preview-link"
                      onClick={() => setCartPreviewOpen(false)}
                    >
                      Προβολή καλαθιού
                    </Link>
                  </>
                ) : (
                  <>
                    <div className="cart-preview-items">
                      {cart.slice(0, 3).map((item) => (
                        <div className="cart-preview-item" key={item.key}>
                          {item.image && (
                            <img src={item.image} alt={item.name} loading="lazy" />
                          )}
                          <div>
                            <strong>{item.name}</strong>
                            <span>
                              {item.quantity} x {item.price}€
                              {item.size ? ` / ${item.size}` : ""}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    {cart.length > 3 && (
                      <span className="cart-preview-more">
                        +{cart.length - 3} ακόμα
                      </span>
                    )}

                    <div className="cart-preview-total">
                      <span>Σύνολο</span>
                      <strong>{cartTotal.toFixed(2)}€</strong>
                    </div>

                    <Link
                      to="/cart"
                      className="cart-preview-link"
                      onClick={() => setCartPreviewOpen(false)}
                    >
                      Προβολή καλαθιού
                    </Link>
                  </>
                )}
              </div>
            </div>
        </nav>
      </div>
      {/* <div className="header-announcement" role="status">
        Το φυσικό κατάστημα στην οδό Μεγάλου Αλεξάνδρου 48 από Δευτέρα έως Τετάρτη 27-29 Ιουλίου θα λειτουργεί τις ώρες 10.00-14.00.
      </div> */}
    </header>
    </>
  );
}
